import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, updateDoc, query, orderBy, deleteDoc, addDoc } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { useTheme } from '../contexts/ThemeContext';
import { Search, CheckCircle, XCircle, Clock, User, Shield, Home, Filter, RefreshCw, Users as UsersIcon, Pencil, Ban, Trash2, MoreVertical } from 'lucide-react';
import toast from 'react-hot-toast';
import Pagination from '../components/Pagination';

const Users = () => {
  const { colors } = useTheme();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ displayName: '', phoneNumber: '', bio: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [actionLoadingUserId, setActionLoadingUserId] = useState(null);
  const [openActionMenuUserId, setOpenActionMenuUserId] = useState(null);
  const itemsPerPage = 8;

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (!event.target.closest('[data-action-menu]')) {
        setOpenActionMenuUserId(null);
      }
    };

    document.addEventListener('click', handleDocumentClick);
    return () => document.removeEventListener('click', handleDocumentClick);
  }, []);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, searchTerm, filterRole, filterStatus]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const usersQuery = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(usersQuery);
      const usersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = users;

    if (searchTerm) {
      filtered = filtered.filter(user =>
        (user.displayName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.email || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterRole !== 'all') {
      filtered = filtered.filter(user => user.role === filterRole || (filterRole === 'guest' && !user.role));
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(user => user.verificationStatus === filterStatus || (filterStatus === 'unverified' && !user.verificationStatus));
    }

    setFilteredUsers(filtered);
    setCurrentPage(1); // Reset to first page on filter
  };

  const updateUserRole = async (userId, role) => {
    // Promoting to admin grants full dashboard + data access — confirm first.
    if (role === 'admin') {
      const target = users.find((u) => u.id === userId);
      const label = target?.displayName || target?.email || 'this user';
      const ok = window.confirm(
        `Make ${label} an admin? They will get full access to the admin dashboard and all data.`
      );
      if (!ok) return;
    }
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        role,
        updatedAt: new Date()
      });

      await writeAuditLog('update_role', userId, { role });
      
      setUsers(users.map(user => 
        user.id === userId 
          ? { ...user, role }
          : user
      ));

      toast.success(`User role updated to ${role}`);
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Failed to update user role');
    }
  };

  const writeAuditLog = async (action, targetUserId, details = {}) => {
    try {
      await addDoc(collection(db, 'audit_logs'), {
        action,
        targetUserId,
        actorUserId: auth.currentUser?.uid || 'unknown',
        details,
        createdAt: new Date(),
        source: 'admin-dashboard-users',
      });
    } catch (error) {
      console.warn('Audit log write failed:', error);
    }
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setEditForm({
      displayName: user.displayName || '',
      phoneNumber: user.phoneNumber || '',
      bio: user.bio || '',
    });
  };

  const closeEditModal = () => {
    setEditingUser(null);
    setEditForm({ displayName: '', phoneNumber: '', bio: '' });
  };

  const saveUserEdits = async () => {
    if (!editingUser) return;
    const displayName = editForm.displayName.trim();
    if (!displayName) {
      toast.error('Display name is required');
      return;
    }

    setSavingEdit(true);
    try {
      const userRef = doc(db, 'users', editingUser.id);
      const payload = {
        displayName,
        phoneNumber: editForm.phoneNumber.trim() || null,
        bio: editForm.bio.trim() || null,
        updatedAt: new Date(),
      };

      await updateDoc(userRef, payload);
      await writeAuditLog('edit_user_profile', editingUser.id, payload);

      setUsers((prev) => prev.map((u) => (u.id === editingUser.id ? { ...u, ...payload } : u)));
      toast.success('User profile updated');
      closeEditModal();
    } catch (error) {
      console.error('Error updating user profile:', error);
      toast.error('Failed to update user profile');
    } finally {
      setSavingEdit(false);
    }
  };

  const toggleUserActivation = async (user) => {
    if (user.role === 'admin') {
      toast.error('Admin accounts are protected');
      return;
    }

    const currentlyActive = user.isActive !== false;
    const confirmText = currentlyActive
      ? 'Deactivate this account? The user will be blocked from normal access.'
      : 'Reactivate this account?';

    if (!window.confirm(confirmText)) return;

    setActionLoadingUserId(user.id);
    try {
      const nextActive = !currentlyActive;
      const payload = {
        isActive: nextActive,
        updatedAt: new Date(),
        ...(nextActive
          ? {
              reactivatedAt: new Date(),
              reactivatedBy: auth.currentUser?.uid || 'admin',
            }
          : {
              deactivatedAt: new Date(),
              deactivatedBy: auth.currentUser?.uid || 'admin',
            }),
      };

      await updateDoc(doc(db, 'users', user.id), payload);
      await writeAuditLog(nextActive ? 'reactivate_user' : 'deactivate_user', user.id, payload);

      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, ...payload } : u)));
      toast.success(nextActive ? 'User reactivated' : 'User deactivated');
    } catch (error) {
      console.error('Error toggling user activation:', error);
      toast.error('Failed to update account status');
    } finally {
      setActionLoadingUserId(null);
    }
  };

  const deleteUser = async (user) => {
    if (user.role === 'admin') {
      toast.error('Admin accounts are protected');
      return;
    }

    if (!window.confirm(`Delete user ${user.displayName || user.email || user.id}? This action is permanent.`)) {
      return;
    }
    const validation = window.prompt('Type DELETE to confirm permanent deletion');
    if (validation !== 'DELETE') {
      toast.error('Deletion cancelled');
      return;
    }

    setActionLoadingUserId(user.id);
    try {
      await deleteDoc(doc(db, 'users', user.id));
      await writeAuditLog('delete_user', user.id, {
        displayName: user.displayName || null,
        email: user.email || null,
      });

      setUsers((prev) => prev.filter((u) => u.id !== user.id));
      toast.success('User deleted');
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    } finally {
      setActionLoadingUserId(null);
    }
  };

  const getAccountBadge = (isActive) => {
    const active = isActive !== false;
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wide uppercase shadow-sm whitespace-nowrap"
        style={{
          backgroundColor: active ? colors.successGhost : colors.errorGhost,
          color: active ? colors.success : colors.error,
          border: `1px solid ${active ? colors.success : colors.error}30`,
        }}
      >
        {active ? 'Active' : 'Deactivated'}
      </span>
    );
  };

  const canManageUser = (user) => user.role !== 'admin';

  const renderUserActions = (user) => {
    const isActive = user.isActive !== false;

    return (
      <div className="relative" data-action-menu>
        {canManageUser(user) ? (
          <>
            <button
              type="button"
              onClick={() => setOpenActionMenuUserId((previous) => (previous === user.id ? null : user.id))}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
              style={{ backgroundColor: colors.backgroundSecondary, border: `1px solid ${colors.border}`, color: colors.text }}
              disabled={actionLoadingUserId === user.id}
            >
              <MoreVertical className="w-3.5 h-3.5" /> More
            </button>

            {openActionMenuUserId === user.id && (
              <div
                className="absolute right-0 mt-2 w-44 rounded-xl shadow-xl overflow-hidden z-20"
                style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setOpenActionMenuUserId(null);
                    openEditModal(user);
                  }}
                  className="w-full px-4 py-3 text-left text-sm flex items-center gap-2 hover:bg-black/5"
                  style={{ color: colors.text }}
                >
                  <Pencil className="w-4 h-4" /> Edit user
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setOpenActionMenuUserId(null);
                    toggleUserActivation(user);
                  }}
                  className="w-full px-4 py-3 text-left text-sm flex items-center gap-2 hover:bg-black/5"
                  style={{ color: isActive ? colors.warning : colors.success }}
                >
                  <Ban className="w-4 h-4" /> {isActive ? 'Deactivate' : 'Reactivate'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setOpenActionMenuUserId(null);
                    deleteUser(user);
                  }}
                  className="w-full px-4 py-3 text-left text-sm flex items-center gap-2 hover:bg-black/5"
                  style={{ color: colors.error }}
                >
                  <Trash2 className="w-4 h-4" /> Delete user
                </button>
              </div>
            )}
          </>
        ) : (
          <span className="text-xs font-bold px-3 py-1.5 rounded-lg" style={{ backgroundColor: colors.backgroundSecondary, color: colors.textMuted }}>
            Protected
          </span>
        )}
      </div>
    );
  };

  const getStatusBadge = (status) => {
    const badges = {
      verified: { bg: colors.successGhost, color: colors.success, icon: CheckCircle, text: 'Verified' },
      pending: { bg: colors.warningGhost, color: colors.warning, icon: Clock, text: 'Pending' },
      rejected: { bg: colors.errorGhost, color: colors.error, icon: XCircle, text: 'Rejected' },
      unverified: { bg: colors.backgroundSecondary, color: colors.textSecondary, icon: User, text: 'Unverified' }
    };

    const badge = badges[status] || badges.unverified;
    const Icon = badge.icon;

    return (
      <span 
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wide uppercase shadow-sm whitespace-nowrap"
        style={{ backgroundColor: badge.bg, color: badge.color, border: `1px solid ${badge.color}30` }}
      >
        <Icon className="w-3.5 h-3.5" />
        {badge.text}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const badges = {
      admin: { bg: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)', color: '#fff', icon: Shield },
      host: { bg: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: '#fff', icon: Home },
      company: { bg: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)', color: '#fff', icon: UsersIcon },
      guest: { bg: colors.surfaceHover, color: colors.text, icon: User }
    };

    const badge = badges[role] || badges.guest;
    const Icon = badge.icon;
    const isGuest = !role || role === 'guest';

    return (
      <span 
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wide uppercase shadow-sm"
        style={{ background: badge.bg, color: badge.color, border: isGuest ? `1px solid ${colors.border}` : 'none' }}
      >
        <Icon className="w-3.5 h-3.5" />
        {role || 'Guest'}
      </span>
    );
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ color: colors.text }}>
            User Management
          </h1>
          <p className="mt-1 text-sm" style={{ color: colors.textSecondary }}>
            View and manage user accounts, roles, and status.
          </p>
        </div>
        <button 
          onClick={fetchUsers}
          className="px-4 py-2 rounded-xl text-[13px] font-semibold transition-colors flex items-center gap-2"
          style={{ backgroundColor: colors.surfaceHover, color: colors.text, border: `1px solid ${colors.border}` }}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh Data
        </button>
      </div>

      {/* Filters */}
      <div className="card-premium p-4" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-[18px] h-[18px]" style={{ color: colors.textMuted }} />
            <input
              type="text"
              placeholder="Search users by name or email..."
              className="w-full pl-11 pr-4 py-2.5 rounded-xl text-[14px] focus:outline-none transition-all duration-200"
              style={{ 
                backgroundColor: colors.backgroundSecondary,
                border: `1px solid ${colors.border}`,
                color: colors.text
              }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={(e) => { e.target.style.borderColor = colors.borderActive; e.target.style.boxShadow = `0 0 0 3px ${colors.primaryGhost}`; }}
              onBlur={(e) => { e.target.style.borderColor = colors.border; e.target.style.boxShadow = 'none'; }}
            />
          </div>
          
          <div className="flex gap-4">
            <div className="relative">
              <Filter className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-[14px] h-[14px]" style={{ color: colors.textMuted }} />
              <select
                className="pl-9 pr-8 py-2.5 rounded-xl text-[13px] font-medium focus:outline-none appearance-none cursor-pointer"
                style={{ backgroundColor: colors.backgroundSecondary, border: `1px solid ${colors.border}`, color: colors.text }}
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
              >
                <option value="all">All Roles</option>
                <option value="guest">Guest</option>
                <option value="host">Host</option>
                <option value="company">Company</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            
            <div className="relative">
              <CheckCircle className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-[14px] h-[14px]" style={{ color: colors.textMuted }} />
              <select
                className="pl-9 pr-8 py-2.5 rounded-xl text-[13px] font-medium focus:outline-none appearance-none cursor-pointer"
                style={{ backgroundColor: colors.backgroundSecondary, border: `1px solid ${colors.border}`, color: colors.text }}
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="verified">Verified</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
                <option value="unverified">Unverified</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="md:hidden space-y-3">
        {filteredUsers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((user) => (
          <div
            key={user.id}
            className="card-premium p-4 space-y-4"
            style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                {user.profileImageUrl ? (
                  <img className="h-11 w-11 rounded-full object-cover border-2 shrink-0" style={{ borderColor: colors.border }} src={user.profileImageUrl} alt="Avatar" />
                ) : (
                  <div className="h-11 w-11 rounded-full flex items-center justify-center font-bold text-white shrink-0" style={{ background: 'linear-gradient(135deg, #64748B 0%, #475569 100%)' }}>
                    {user.displayName?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="text-[14px] font-bold truncate" style={{ color: colors.text }}>{user.displayName || 'No name'}</div>
                  <div className="text-[12px] font-medium truncate" style={{ color: colors.textSecondary }}>{user.email || 'No email'}</div>
                </div>
              </div>
              {renderUserActions(user)}
            </div>

            <div className="grid grid-cols-2 gap-2 text-[12px]">
              <div>{getRoleBadge(user.role)}</div>
              <div>{getStatusBadge(user.verificationStatus)}</div>
              <div>{getAccountBadge(user.isActive)}</div>
              <div className="text-[13px] font-medium flex items-center justify-end" style={{ color: colors.textSecondary }}>
                {user.createdAt?.toDate?.()?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) || 'Unknown'}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <select
                className="flex-1 px-3 py-2 rounded-lg text-xs font-bold focus:outline-none transition-all cursor-pointer hover:shadow-sm"
                style={{
                  backgroundColor: colors.backgroundSecondary,
                  border: `1px solid ${colors.border}`,
                  color: colors.text
                }}
                value={user.role || 'guest'}
                onChange={(e) => updateUserRole(user.id, e.target.value)}
                disabled={actionLoadingUserId === user.id || !canManageUser(user)}
              >
                <option value="guest">Guest</option>
                <option value="host">Host</option>
                <option value="company">Company</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
        ))}
      </div>

      <div className="hidden md:block card-premium overflow-hidden animate-fadeInScale" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
        {loading ? (
           <div className="flex flex-col items-center justify-center h-64">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: colors.primary }}></div>
              <p className="mt-4 text-sm font-medium" style={{ color: colors.textSecondary }}>Refreshing accounts...</p>
           </div>
        ) : filteredUsers.length === 0 ? (
           <div className="flex flex-col items-center justify-center p-12">
             <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: colors.backgroundSecondary }}>
                <UsersIcon className="w-8 h-8" style={{ color: colors.textMuted }} />
             </div>
             <h3 className="text-[15px] font-semibold" style={{ color: colors.text }}>No users found</h3>
             <p className="mt-1 text-[13px]" style={{ color: colors.textSecondary }}>
                Try adjusting your search or filter criteria.
             </p>
           </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr style={{ backgroundColor: colors.surfaceHover, borderBottom: `1px solid ${colors.border}` }}>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>User Info</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Role</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Verification</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Account</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Joined</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider text-right" style={{ color: colors.textMuted }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((user, index) => (
                  <tr 
                    key={user.id} 
                    className="group transition-colors"
                    style={{ borderBottom: index < filteredUsers.length - 1 ? `1px solid ${colors.border}` : 'none', backgroundColor: colors.surface }}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        {user.profileImageUrl ? (
                          <img className="h-10 w-10 text-[10px] rounded-full object-cover border-2" style={{ borderColor: colors.border }} src={user.profileImageUrl} alt="Avatar" />
                        ) : (
                          <div className="h-10 w-10 rounded-full flex items-center justify-center font-bold text-white shadow-sm" style={{ background: 'linear-gradient(135deg, #64748B 0%, #475569 100%)' }}>
                            {user.displayName?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                        )}
                        <div>
                          <div className="text-[14px] font-bold" style={{ color: colors.text }}>{user.displayName || 'No name'}</div>
                          <div className="text-[12px] font-medium" style={{ color: colors.textSecondary }}>{user.email || 'No email'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getRoleBadge(user.role)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(user.verificationStatus)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getAccountBadge(user.isActive)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-[13px] font-medium" style={{ color: colors.textSecondary }}>
                      {user.createdAt?.toDate?.()?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right h-full">
                      <div className="flex items-center justify-end h-full gap-2">
                        <select
                          className="px-3 py-1.5 rounded-lg text-xs font-bold focus:outline-none transition-all cursor-pointer hover:shadow-sm"
                          style={{ 
                            backgroundColor: colors.backgroundSecondary,
                            border: `1px solid ${colors.border}`,
                            color: colors.text
                          }}
                          value={user.role || 'guest'}
                          onChange={(e) => updateUserRole(user.id, e.target.value)}
                          disabled={actionLoadingUserId === user.id || !canManageUser(user)}
                        >
                          <option value="guest">Guest</option>
                          <option value="host">Host</option>
                          <option value="company">Company</option>
                          <option value="admin">Admin</option>
                        </select>

                        {renderUserActions(user)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && filteredUsers.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalItems={filteredUsers.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        )}
      </div>

      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.45)' }}>
          <div className="w-full max-w-lg rounded-2xl p-6" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
            <h3 className="text-lg font-bold" style={{ color: colors.text }}>
              Edit User Profile
            </h3>
            <p className="mt-1 text-sm" style={{ color: colors.textSecondary }}>
              Update profile fields for {editingUser.email || editingUser.id}
            </p>

            <div className="mt-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: colors.textMuted }}>
                  Display Name
                </label>
                <input
                  type="text"
                  value={editForm.displayName}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, displayName: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg"
                  style={{ backgroundColor: colors.backgroundSecondary, border: `1px solid ${colors.border}`, color: colors.text }}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: colors.textMuted }}>
                  Phone Number
                </label>
                <input
                  type="text"
                  value={editForm.phoneNumber}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, phoneNumber: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg"
                  style={{ backgroundColor: colors.backgroundSecondary, border: `1px solid ${colors.border}`, color: colors.text }}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: colors.textMuted }}>
                  Bio
                </label>
                <textarea
                  value={editForm.bio}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, bio: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 rounded-lg"
                  style={{ backgroundColor: colors.backgroundSecondary, border: `1px solid ${colors.border}`, color: colors.text }}
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={closeEditModal}
                className="px-4 py-2 rounded-lg text-sm font-semibold"
                style={{ backgroundColor: colors.backgroundSecondary, border: `1px solid ${colors.border}`, color: colors.text }}
                disabled={savingEdit}
              >
                Cancel
              </button>
              <button
                onClick={saveUserEdits}
                className="px-4 py-2 rounded-lg text-sm font-semibold"
                style={{ backgroundColor: colors.primary, color: '#fff' }}
                disabled={savingEdit}
              >
                {savingEdit ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Users;